import { useFormikContext } from 'formik';
import {
    GenericObject,
    checkEmail,
    checkIfSerbianMobPhoneNumber,
    checkPhoneNumber,
    checkSerbianBankAccount,
    checkUCN,
    formatPhoneValue,
    getApplicationId,
    getIsPortal,
    hasValueAndIsSalesForce,
} from '@fnol/library/src/lib';

import { constValues, legalPersonType, personType, translations } from './consts';

export const useValidateIndividual = (
    individualType: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const individualExistingErrors: { [key: string]: string } = {};
    const individualTouched: { [key: string]: boolean } = {};
    let individualIsValid = true;

    const { values } = useFormikContext<GenericObject>();

    const name = `${personType[individualType]}IndividualName`;
    const lastname = `${personType[individualType]}IndividualLastName`;
    const ucn = `${personType[individualType]}IndividualUCN`;
    const passportNumber = `${personType[individualType]}IndividualPassportNumber`;
    const dateOfBirth = `${personType[individualType]}IndividualDateOfBirth`;
    const country = `${personType[individualType]}IndividualCountry`;
    const city = `${personType[individualType]}IndividualCity`;
    const cityInSerbia = `${personType[individualType]}IndividualCityInSerbia`;
    const address = `${personType[individualType]}IndividualAddress`;
    const addressNumber = `${personType[individualType]}IndividualAddressNumber`;
    const email = `${personType[individualType]}IndividualEmail`;
    const phone = `${personType[individualType]}IndividualPhone`;
    const accountNumber = `${personType[individualType]}IndividualAccountNumber`;
    const duplicatedUcn = `${personType[individualType]}DuplicatedUcn`;

    individualTouched[name] = true;
    individualTouched[lastname] = true;
    individualTouched[ucn] = true;
    individualTouched[passportNumber] = true;
    individualTouched[dateOfBirth] = true;
    individualTouched[country] = true;
    individualTouched[city] = true;
    individualTouched[cityInSerbia] = true;
    individualTouched[address] = true;
    individualTouched[addressNumber] = true;
    individualTouched[email] = true;
    individualTouched[phone] = true;
    individualTouched[accountNumber] = true;

    if (!values[name]) {
        individualExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (!values[lastname]) {
        individualExistingErrors[lastname] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (
        !values[ucn] &&
        (values[country] === constValues.countrySerbiaId ||
            values[country] === constValues.countrySerbiaId.toString(10))
    ) {
        individualExistingErrors[ucn] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (values[ucn] && values.reportedClaims && values.reportedClaims.find((item: any) => item.jmbg === values[ucn])) {
        individualExistingErrors[ucn] = translations.validationMessages.INSURED_CASE_ALREADY_EXIST;
        individualIsValid = false;
    }

    if (
        values[country] === constValues.countrySerbiaId ||
        values[country] === constValues.countrySerbiaId.toString(10)
    ) {
        if (values[ucn] && !checkUCN(values[ucn])) {
            individualExistingErrors[ucn] = translations.validationMessages.UCN_INVALID_FORMAT;
            individualIsValid = false;
        } else {
            if (values[ucn] && values[duplicatedUcn]) {
                individualExistingErrors[ucn] = ' ';
                individualIsValid = false;
            }
        }
    }

    if (values[country] === '') {
        individualExistingErrors[country] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (
        values[country] !== constValues.countrySerbiaId.toString(10) &&
        values[country] !== constValues.countrySerbiaId &&
        values[city] === ''
    ) {
        individualExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (
        (values[country] === constValues.countrySerbiaId.toString(10) ||
            values[country] === constValues.countrySerbiaId) &&
        (!values[cityInSerbia] || values[cityInSerbia].id === '')
    ) {
        individualExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (!values[dateOfBirth]) {
        individualExistingErrors[dateOfBirth] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (
        values[country] !== constValues.countrySerbiaId &&
        values[country] !== constValues.countrySerbiaId.toString(10) &&
        !values[passportNumber]
    ) {
        individualExistingErrors[passportNumber] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (values[address] === '') {
        individualExistingErrors[address] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (values[addressNumber] === '') {
        individualExistingErrors[addressNumber] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (!values[email] && !(hasValueAndIsSalesForce(values[phone]) && values[phone]?.length > 4)) {
        individualExistingErrors[email] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (values[email] && !checkEmail(values[email])) {
        // check only if there is a value
        individualExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
        individualIsValid = false;
    }

    if (formatPhoneValue(values[phone]) === '' && !hasValueAndIsSalesForce(values[email])) {
        individualExistingErrors[phone] = translations.validationMessages.REQUIRED_FIELD;
        individualIsValid = false;
    }

    if (formatPhoneValue(values[phone]) !== '' && !checkPhoneNumber(values[phone])) {
        individualExistingErrors[phone] = translations.validationMessages.PHONE_NUMBER_FORMAT;
        individualIsValid = false;
    }

    if (formatPhoneValue(values[phone]) !== '' && !checkIfSerbianMobPhoneNumber(values[phone])) {
        individualExistingErrors[phone] = translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
        individualIsValid = false;
    }

    if (getIsPortal(getApplicationId()) && individualType === personType.submitter) {
        if (!values[accountNumber]) {
            individualExistingErrors[accountNumber] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }
        if (values[accountNumber] && !checkSerbianBankAccount(values[accountNumber])) {
            individualExistingErrors[accountNumber] = translations.validationMessages.ACCOUNT_NUMBER_INVALID_FORMAT;
            individualIsValid = false;
        }
    }

    return { errors: individualExistingErrors, touched: individualTouched, isValid: individualIsValid };
};

export const useValidateLegalEntity = (
    legalEntityType: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const legalEntityExistingErrors: { [key: string]: string } = {};
    const legalEntityTouched: { [key: string]: boolean } = {};
    let legalEntityIsValid = true;

    const { values } = useFormikContext<any>();

    const legalPersonTypePropertyName = `${personType[legalEntityType]}Type`;

    const name = `${personType[legalEntityType]}LegalEntityName`;
    const id = `${personType[legalEntityType]}LegalEntityID`;
    const vat = `${personType[legalEntityType]}LegalEntityVAT`;
    const country = `${personType[legalEntityType]}LegalEntityCountry`;
    const city = `${personType[legalEntityType]}LegalEntityCity`;
    const cityInSerbia = `${personType[legalEntityType]}LegalEntityCityInSerbia`;
    const address = `${personType[legalEntityType]}LegalEntityAddress`;
    const addressNumber = `${personType[legalEntityType]}LegalEntityAddressNumber`;
    const email = `${personType[legalEntityType]}LegalEntityEmail`;
    const duplicatedId = `${personType[legalEntityType]}DuplicatedId`;

    if (values[legalPersonTypePropertyName] === legalPersonType.legal) {
        legalEntityTouched[name] = true;
        legalEntityTouched[id] = true;
        legalEntityTouched[vat] = true;
        legalEntityTouched[country] = true;
        legalEntityTouched[city] = true;
        legalEntityTouched[cityInSerbia] = true;
        legalEntityTouched[email] = true;
        legalEntityTouched[address] = true;
        legalEntityTouched[addressNumber] = true;

        if (!values[name]) {
            legalEntityExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!values[id]) {
            legalEntityExistingErrors[id] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        } else {
            if (values[duplicatedId]) {
                legalEntityExistingErrors[id] = ' ';
                legalEntityIsValid = false;
            }
        }

        if (!!values.legalEntityIdFieldFormatError) {
            legalEntityExistingErrors[id] = translations.validationMessages.LEGAL_ENTITY_NUMBER_INVALID_FORMAT;
            legalEntityIsValid = false;
        }

        if (!values[vat] || values[vat] === '') {
            legalEntityExistingErrors[vat] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!!values.VATFormatError) {
            legalEntityExistingErrors[vat] = translations.validationMessages.VAT_INVALID_FORMAT;
            legalEntityIsValid = false;
        }

        if (!values[cityInSerbia] || values[cityInSerbia].id === '') {
            legalEntityExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (values[address] === '') {
            legalEntityExistingErrors[address] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (values[addressNumber] === '') {
            legalEntityExistingErrors[addressNumber] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!values[email]) {
            legalEntityExistingErrors[email] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (values[email] && !checkEmail(values[email])) {
            // check only if there is a value
            legalEntityExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            legalEntityIsValid = false;
        }
    }

    return { errors: legalEntityExistingErrors, touched: legalEntityTouched, isValid: legalEntityIsValid };
};
